import * as React from "react";
import { Polygon } from "geojson";
import { useApi } from "../containers/ApiContainer";
import { uuidv4 } from "../utils";

export type PredefinedObstacle = {
  id: string;
  name: string;
  width: number;
  length: number;
  height: number;
  image?: string;
  geom?: Polygon;
  geomz?: Polygon;
  is_default?: boolean;
  created_at?: string;
};

export const usePredefinedObstacle = () => {
  const { postgrest } = useApi();
  const [loading, setLoading] = React.useState<boolean>(true);
  const [error, setError] = React.useState<string | null>(null);
  const [predefinedObstacles, setPredefinedObstacles] = React.useState<
    PredefinedObstacle[]
  >([]);

  const fetchPredefinedObstacles = React.useCallback(async () => {
    try {
      setError(null);
      const { data, error: fetchError } = await postgrest
        .from("predefined_obstacle")
        .select()
        .order("name");
      
      if (fetchError) {
        throw fetchError;
      }
      
      setPredefinedObstacles(data || []);
      setLoading(false);
    } catch (err) {
      console.error("Error fetching predefined obstacles:", err);
      setError(err?.message || "Failed to fetch obstacles");
      setLoading(false);
    }
  }, [postgrest]);

  React.useEffect(() => {
    fetchPredefinedObstacles();
  }, [fetchPredefinedObstacles]);

  const upsertPredefinedObstacle = React.useCallback(
    async (obstacle: PredefinedObstacle) => {
      try {
        setError(null);
        
        // Call the admin upsert function
        const { data, error: rpcError } = await postgrest.rpc(
          "admin_upsert_predefined_obstacle",
          {
            p_id: obstacle.id,
            p_name: obstacle.name,
            p_width: obstacle.width,
            p_length: obstacle.length,
            p_height: obstacle.height,
            p_image: obstacle.image
          }
        );
        
        if (rpcError) {
          throw rpcError;
        }
        
        // Refresh the list
        await fetchPredefinedObstacles();
        
        return data;
      } catch (err) {
        console.error("Error upserting obstacle:", err);
        setError(err?.message || "Failed to save obstacle");
        throw err;
      }
    },
    [postgrest, fetchPredefinedObstacles]
  );

  const createPredefinedObstacle = React.useCallback(
    async (obstacle: Omit<PredefinedObstacle, "id">) => {
      const newObstacle: PredefinedObstacle = {
        ...obstacle,
        id: uuidv4(),
        created_at: new Date().toISOString(),
      };
      
      const id = await upsertPredefinedObstacle(newObstacle);
      return { ...newObstacle, id: id || newObstacle.id };
    },
    [upsertPredefinedObstacle]
  );

  const deletePredefinedObstacle = React.useCallback(
    async (id: string) => {
      try {
        setError(null);
        
        // Call the admin delete function
        const { data, error: rpcError } = await postgrest.rpc(
          "admin_delete_predefined_obstacle",
          { p_id: id }
        );
        
        if (rpcError) {
          throw rpcError;
        }
        
        // Refresh the list
        await fetchPredefinedObstacles();
        
        return data;
      } catch (err) {
        console.error("Error deleting obstacle:", err);
        setError(err?.message || "Failed to delete obstacle");
        throw err;
      }
    },
    [postgrest, fetchPredefinedObstacles]
  );

  const updatePredefinedObstacle = React.useCallback(
    async (obstacle: PredefinedObstacle) => {
      return upsertPredefinedObstacle(obstacle);
    },
    [upsertPredefinedObstacle]
  );

  return {
    loading,
    error,
    predefinedObstacles,
    createPredefinedObstacle,
    updatePredefinedObstacle,
    deletePredefinedObstacle,
    refreshObstacles: fetchPredefinedObstacles,
  };
};
